/*
 * Copyright (C) 2018 BarD Software s.r.o
 */
import {PostAuth} from './Routes';
import firebase from 'firebase/compat/app';
import * as firebaseui from 'firebaseui';

import $ from 'jquery';

/**
 * Starts the Firebase UI widget.
 *
 * @param isSignup on the sign-in page the widget may not create accounts: an unknown email address
 * produces an "unauthorized user" card instead of the password creation form. A federated provider
 * ignores that setting and signs the user in anyway.
 * @param onSignedIn called on a successful sign-in instead of redirecting, so that the caller can ask
 * the backend whether the user has an account with us and decide where to go.
 */
export function showUi(isSignup: boolean, onSignedIn: () => void) {
    // One instance per app, and a live widget refuses to start again -- both matter because the
    // caller puts the widget back after a sign-in it could not accept. A fresh instance is left
    // alone: resetting it would discard a redirect result it has not read yet.
    const rendered = firebaseui.auth.AuthUI.getInstance();
    rendered?.reset();
    const ui = rendered || new firebaseui.auth.AuthUI(firebase.auth());
    const verb = isSignup ? 'Sign up' : 'Sign in';
    const uiConfig = {
        callbacks: {
            signInSuccessWithAuthResult() {
                onSignedIn();
                // Cancels the redirect to signInSuccessUrl; the widget empties the container after
                // that, leaving it for the caller to render into.
                return false;
            },
            uiShown() {
                $('#loader').hide();
            },
        },
        signInFlow: 'redirect',
        signInSuccessUrl: PostAuth.absoluteUrl(),
        signInOptions: [
            {
                provider: firebase.auth.GoogleAuthProvider.PROVIDER_ID,
                fullLabel: `${verb} with Google`,
                scopes: [
                    'https://www.googleapis.com/auth/userinfo.email',
                    'https://www.googleapis.com/auth/userinfo.profile'
                ],
                customParameters: {
                    // Forces account selection even when one account
                    // is available.
                    prompt: 'select_account',
                },
            },
            {
                provider: firebase.auth.EmailAuthProvider.PROVIDER_ID,
                requireDisplayName: false,
                fullLabel: `${verb} with email`,
                // The wording of the "unauthorized user" card is hardcoded in the firebaseui bundle and
                // says "Not Authorized", which is not what we mean. Signin.vue hides that card and shows
                // its own message instead.
                disableSignUp: {status: !isSignup},
            },
        ],
        // No tosUrl/privacyPolicyUrl on purpose: the widget would repeat the links that the page
        // already shows below it. Consent is taken by the checkbox on the post-auth page anyway.
    };
    ui.start('#firebaseui-auth-container', uiConfig);
}
